"use client";

/**
 * Non-intrusive sticky conversion bar (Phase 13).
 *
 * Appears only AFTER the visitor scrolls past the hero, hides again near the
 * bottom so it never covers the final CTA, and hides while a modal dialog is
 * open. Dismissible for the session (sessionStorage). Desktop: a centered
 * floating pill; mobile: a full-width bottom bar. Reduced-motion safe (it
 * mounts/unmounts, no slide animation).
 *
 * Analytics: sticky_conversion_view (once), sticky_workflow_click,
 * sticky_trial_click, sticky_conversion_dismiss — no page content is sent.
 */
import * as React from "react";
import posthog from "posthog-js";
import type { AnalyticsEvent, SafeEventProps } from "@/lib/analytics/events";

function capture(event: AnalyticsEvent, props: SafeEventProps) {
  try {
    posthog.capture(event, props);
  } catch {
    /* analytics must never break the page */
  }
}

const DISMISS_KEY = "pcn_guide_sticky_dismissed";

export function StickyConversion({
  sourcePage,
  workflowHref,
  trialHref,
}: {
  sourcePage: string;
  workflowHref: string;
  trialHref: string;
}) {
  const [dismissed, setDismissed] = React.useState(true);
  const [visible, setVisible] = React.useState(false);
  const viewedRef = React.useRef(false);

  React.useEffect(() => {
    try {
      if (sessionStorage.getItem(DISMISS_KEY)) return; // stays dismissed
    } catch {
      /* sessionStorage may be unavailable */
    }
    setDismissed(false);

    const onScroll = () => {
      const y = window.scrollY;
      const docH = document.documentElement.scrollHeight;
      const vh = window.innerHeight;
      const pastHero = y > 700;
      const nearBottom = y + vh > docH - 640; // keep clear of the final CTA
      const modalOpen = !!document.querySelector(
        '[aria-modal="true"], [role="dialog"][data-state="open"]',
      );
      const show = pastHero && !nearBottom && !modalOpen;
      setVisible(show);
      if (show && !viewedRef.current) {
        viewedRef.current = true;
        capture("sticky_conversion_view", {
          source_page: sourcePage,
          page_type: "guide",
        });
      }
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [sourcePage]);

  if (dismissed || !visible) return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
    capture("sticky_conversion_dismiss", {
      source_page: sourcePage,
      page_type: "guide",
    });
  };

  const baseProps = { source_page: sourcePage, page_type: "guide" as const };

  return (
    <div
      role="region"
      aria-label="Quick actions"
      className="fixed inset-x-3 bottom-3 z-40 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-auto sm:max-w-[620px]"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border rule bg-paper px-4 py-3 shadow-[0_3px_16px_rgba(0,0,0,0.08)]">
        <span className="mr-auto text-[13.5px] font-medium text-[color:var(--ink)]">
          Ready to see BOM impact?
        </span>
        <a
          href={workflowHref}
          onClick={() =>
            capture("sticky_workflow_click", {
              ...baseProps,
              cta_text: "See the workflow",
              destination: "product",
            })
          }
          className="inline-flex items-center justify-center px-3.5 py-2 border rule text-[13px] font-medium ink-2 hover:border-[color:var(--ink)] hover:text-[color:var(--ink)] transition"
        >
          See the workflow
        </a>
        <a
          href={trialHref}
          onClick={() =>
            capture("sticky_trial_click", {
              ...baseProps,
              cta_text: "Start trial",
              destination: "signup",
            })
          }
          className="inline-flex items-center justify-center px-3.5 py-2 bg-[color:var(--ink)] text-white text-[13px] font-medium hover:bg-black transition"
        >
          Start trial
        </a>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss quick actions"
          className="inline-flex h-7 w-7 items-center justify-center text-[16px] leading-none ink-3 hover:text-[color:var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--shark-accent)]"
        >
          &times;
        </button>
      </div>
    </div>
  );
}
